import type { ExperimentArm } from "./types";

export const ALLOWED_FIELDS = {
  demo_view: ["viewport_class", "reduced_motion"],
  ritual_selected: ["ritual"],
  frog_started: ["frog_factor", "amount_band", "currency_code"],
  frog_tap: ["tap_count"],
  frog_completed: ["frog_factor", "enjoy_percent", "duration_band"],
  bubble_selected: ["bubble_index"],
  keep_confirmed: ["frog_factor", "confirmed"],
  wish_started: ["wish_factor"],
  branch_progress: ["progress_band"],
  wish_revealed: ["wish_factor", "message_id", "message_tone"],
  feedback_submitted: ["rating", "ritual"],
  phase_completed: ["duration_band"],
  new_pot_started: ["days_since_prior_band"],
} as const;

export type AnalyticsEventName = keyof typeof ALLOWED_FIELDS;

export interface AnalyticsContext {
  demoVersion: string;
  sessionId: string;
  participantId: string;
  arm: ExperimentArm;
  source: string;
  isReturnSession: boolean;
  debug: boolean;
  testMode: boolean;
}

export interface AnalyticsSink {
  track(record: Readonly<Record<string, unknown>>): void;
}

const FIELD_VALIDATORS: Record<string, (value: unknown) => boolean> = {
  viewport_class: inSet("mobile", "tablet", "desktop"),
  reduced_motion: isBoolean,
  ritual: inSet("frog", "tree"),
  amount_band: inSet("20-49", "50-99", "100-149", "150-200"),
  currency_code: inSet("USD", "UAH"),
  frog_factor: inSet("fixed", "random"),
  tap_count: integerBetween(1, 5),
  enjoy_percent: integerBetween(55, 65),
  duration_band: inSet("under-15s", "15-30s", "30-60s", "over-60s"),
  bubble_index: integerBetween(1, 3),
  confirmed: isBoolean,
  wish_factor: inSet("plain", "tactile"),
  progress_band: inSet(25, 50, 75, 100),
  message_id: shortSlug,
  message_tone: inSet("encouraging"),
  rating: integerBetween(1, 5),
  days_since_prior_band: inSet("same-day", "1-day", "2-6-days", "7-plus-days"),
};

export class AnalyticsAdapter {
  private readonly events: Array<Record<string, unknown>> = [];

  constructor(
    private readonly context: AnalyticsContext,
    private readonly sink?: AnalyticsSink,
  ) {
    window.__MONEY_RITUALS_EVENTS__ = this.events;
  }

  track(event: AnalyticsEventName, properties: Record<string, unknown> = {}): void {
    const record = buildEventRecord(event, properties, this.context);
    this.events.push(record);
    window.__MONEY_RITUALS_EVENTS__ = [...this.events];

    if (this.context.debug) {
      console.info("[money-rituals]", record);
      window.dispatchEvent(new CustomEvent("money-rituals:event", { detail: record }));
    }

    if (this.context.testMode) return;
    this.sink?.track(record);
    const endpoint = validEndpoint(window.MONEY_RITUALS_CONFIG);
    if (!endpoint) return;
    const body = JSON.stringify(record);
    const endpointUrl = new URL(endpoint);
    if (
      endpointUrl.origin === window.location.origin &&
      navigator.sendBeacon &&
      navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }))
    ) {
      return;
    }
    void fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
  }
}

export function buildEventRecord(
  event: AnalyticsEventName,
  properties: Record<string, unknown>,
  context: AnalyticsContext,
): Record<string, unknown> {
  const allowed = new Set<string>(ALLOWED_FIELDS[event]);
  const sanitizedProperties = Object.fromEntries(
    Object.entries(properties).filter(
      ([key, value]) =>
        allowed.has(key) && isSafePrimitive(value) && (FIELD_VALIDATORS[key]?.(value) ?? false),
    ),
  );

  return {
    event,
    schema_version: 1,
    demo_version: safeVersion(context.demoVersion),
    session_id: /^s_[0-9a-f]{20}$/.test(context.sessionId) ? context.sessionId : "invalid_session",
    participant_id: /^p_[0-9a-f]{20}$/.test(context.participantId) ? context.participantId : "invalid_participant",
    arm: context.arm,
    source: /^[a-z0-9_-]{1,40}$/.test(context.source) ? context.source : "direct",
    is_return_session: context.isReturnSession,
    test_mode: context.testMode,
    timestamp: new Date().toISOString(),
    ...sanitizedProperties,
  };
}

function validEndpoint(config: MoneyRitualsConfig | undefined): string | null {
  if (!config?.analyticsEndpoint) return null;
  try {
    const endpoint = new URL(config.analyticsEndpoint, window.location.href);
    const isLoopback = ["localhost", "127.0.0.1", "[::1]"].includes(endpoint.hostname);
    if (endpoint.protocol !== "https:" && !isLoopback) return null;
    const allowedOrigins = new Set([window.location.origin, ...(config.allowedAnalyticsOrigins ?? [])]);
    return allowedOrigins.has(endpoint.origin) ? endpoint.toString() : null;
  } catch {
    return null;
  }
}

function isSafePrimitive(value: unknown): value is string | number | boolean | null {
  if (typeof value === "number") return Number.isFinite(value);
  return value === null || typeof value === "string" || typeof value === "boolean";
}

function inSet(...allowed: ReadonlyArray<string | number>): (value: unknown) => boolean {
  return (value) => allowed.includes(value as string | number);
}

function integerBetween(minimum: number, maximum: number): (value: unknown) => boolean {
  return (value) => typeof value === "number" && Number.isInteger(value) && value >= minimum && value <= maximum;
}

function isBoolean(value: unknown): boolean {
  return typeof value === "boolean";
}

function shortSlug(value: unknown): boolean {
  return typeof value === "string" && /^[a-z0-9-]{1,40}$/.test(value);
}

function safeVersion(value: string): string {
  return /^[a-z0-9._-]{1,40}$/i.test(value) ? value : "invalid_version";
}
